import React, {useState, useEffect} from 'react';
import {v4 as uuid} from 'uuid';
import {Alert, Stack} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
  Label,
  Button,
  Modal,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import usePurchaseProcessEndedEvent from '@contentlab/hooks/analytics/use-purchase-process-ended-event';
import usePurchaseProcessStartedEvent from '@contentlab/hooks/analytics/use-purchase-process-started-event';
import useStoreViewPresentedEvent from '@contentlab/hooks/analytics/use-store-view-presented-event';
import styles from '@contentlab/views/contentlab/pricing/SubscriptionChangeModalContent.module.scss';
import {PricingCtaButton} from '@contentlab/views/contentlab/pricing/components/pricing-bundle/Buttons';
import {getPriceCurrency} from '@contentlab/views/contentlab/pricing/utils';
import {
  CONTENT_LAB_VIEW_NAMES,
  CONTENT_STUDIO_PRICING_VIEW_TYPES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import Duration from '@/types/models/pricing/duration';
import SubscriptionPlan from '@/types/models/pricing/subscriptionPlan';
import BackButton from '@/components/back-button';
import Conditional from '@/components/conditional';
import Divider from '@/components/divider';
import Skeleton from '@/components/skeleton';
import useContentStudioChangeSubscriptionMutation from '@/hooks/content-studio/mutations/use-content-studio-change-subscription-mutation';

type ChangeSubscriptionDetails = {
  changeType: 'upgrade' | 'downgrade' | 'crossgrade' | 'rollback';
  adjustmentAmount?: number;
  consumableUsageRatio?: {
    usageRatio: number;
    totalGrantedConsumableQuantity: number;
    availableConsumableQuantity: number;
    usedOrExpiredConsumableQuantity: number;
    consumableType: string;
  };
};

type SubscriptionChangeProps = {
  currentSubscriptionId: string;
  currentPlan: SubscriptionPlan;
  selectedPlan: SubscriptionPlan;
  onBackButtonClick: (navigation: {
    goBack: () => void;
    navigate: (to: string, params?: any) => void;
  }) => void;
  handleClose: (shouldSendProcessCanceledEvent?: boolean) => void;
  presentationId: string;
  purchaseProcessId: string;
  onSuccessCallback?: () => void;
  refetchCurrentSubscription: () => void;
};

function SubscriptionChangeContent({
  currentSubscriptionId,
  currentPlan,
  selectedPlan,
  onBackButtonClick,
  handleClose,
  presentationId,
  purchaseProcessId,
  onSuccessCallback,
  refetchCurrentSubscription,
}: SubscriptionChangeProps) {
  const {mutateAsync: changeSubscription} =
    useContentStudioChangeSubscriptionMutation();
  const [showError, setShowError] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isDetailsFetchInProgress, setIsDetailsFetchInProgress] =
    useState<boolean>(false);
  const [isChangeRequestInProgress, setIsChangeRequestInProgress] =
    useState<boolean>(false);
  const [changeDetails, setChangeDetails] =
    useState<ChangeSubscriptionDetails | null>(null);

  const {storeViewPresented} = useStoreViewPresentedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {purchaseProcessStarted} = usePurchaseProcessStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {purchaseProcessEnded} = usePurchaseProcessEndedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {uiInteraction} = useUiInteractionEvent();

  useEffect(() => {
    storeViewPresented({
      presentation_id: presentationId,
      view_name: CONTENT_LAB_VIEW_NAMES.PRICING,
      view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.CHANGE_CONFIRM_MODAL,
      view_details: selectedPlan.id,
    });
    purchaseProcessStarted({
      presentation_id: presentationId,
      process_id: purchaseProcessId,
      product_id: selectedPlan.id,
      product_name: selectedPlan.id,
    });
    const fetchDetails = async () => {
      setIsDetailsFetchInProgress(true);
      try {
        const result = await changeSubscription({
          paymentSourceId: currentSubscriptionId,
          selectedPlanId: selectedPlan.id,
          isDryRun: true,
        });
        setChangeDetails(result);
      } catch (error) {
        console.error('Fetch subscription change details failed', error);
      } finally {
        setIsDetailsFetchInProgress(false);
      }
    };
    fetchDetails();
  }, []);

  const sendUiInteractionEvent = (buttonName: string) => {
    uiInteraction({
      item_name: buttonName,
      interaction_location:
        CONTENT_STUDIO_PRICING_VIEW_TYPES.CHANGE_CONFIRM_MODAL,
      flow_name: FLOW_NAMES.CONTENT_STUDIO.PRICING,
      presentation_id: presentationId,
      interaction_id: uuid(),
    });
  };

  const handleBackButtonClick = (navigation: {
    goBack: () => void;
    navigate: (to: string, params?: any) => void;
  }) => {
    sendUiInteractionEvent('Back');
    onBackButtonClick(navigation);
  };

  const handleConfirmChangeClick = async () => {
    sendUiInteractionEvent('Confirm Change');
    setShowError(false);
    setIsChangeRequestInProgress(true);
    try {
      await changeSubscription({
        paymentSourceId: currentSubscriptionId,
        selectedPlanId: selectedPlan.id,
        isDryRun: false,
      });
      setIsSuccessModalOpen(true);
      purchaseProcessEnded({
        presentation_id: presentationId,
        process_id: purchaseProcessId,
        product_id: selectedPlan.id,
        product_name: selectedPlan.id,
        result: 'success',
      });
    } catch (error) {
      setShowError(true);
      purchaseProcessEnded({
        presentation_id: presentationId,
        process_id: purchaseProcessId,
        product_id: selectedPlan.id,
        product_name: selectedPlan.id,
        result: 'failure',
      });
    } finally {
      setIsChangeRequestInProgress(false);
    }
  };

  const handleSuccessModalClose = (buttonName: string) => {
    sendUiInteractionEvent(buttonName);
    setIsSuccessModalOpen(false);
    handleClose(false);
    onSuccessCallback?.();
    refetchCurrentSubscription();
  };

  return (
    <Stack className={styles.container}>
      <Stack>
        <Stack alignItems="flex-start">
          <BackButton
            onBackClick={handleBackButtonClick}
            backIcon={
              <Icon
                size="medium"
                appearance="neutral"
                name="Actions-Arrow-Back-Small"
                style={{cursor: 'pointer'}}
              />
            }
          />
        </Stack>
        <Headline size="md" className={styles.title}>
          {translate('Subscription Change Summary')}
        </Headline>
        <PlansDetails currentPlan={currentPlan} selectedPlan={selectedPlan} />
        <PriceSection
          isLoading={isDetailsFetchInProgress}
          selectedPlan={selectedPlan}
          unusedVideosCount={
            changeDetails?.consumableUsageRatio?.availableConsumableQuantity
          }
          unusedVideosValue={changeDetails?.adjustmentAmount}
        />
        <Disclaimer
          shouldShowUnusedCredits={(changeDetails?.adjustmentAmount || 0) > 0}
        />
      </Stack>
      <Stack direction="column" gap="16px">
        {showError && <ErrorMessage />}
        <Buttons
          handleConfirmChangeClick={handleConfirmChangeClick}
          handleCancelClick={() => {
            sendUiInteractionEvent('Cancel');
            handleClose();
          }}
          isLoading={isChangeRequestInProgress || isDetailsFetchInProgress}
        />
      </Stack>
      <Modal
        open={isSuccessModalOpen}
        showCloseButton
        handleClose={() => handleSuccessModalClose('Exit')}
        primaryButtonText={translate('Done')}
        primaryButtonProps={{
          className: styles.cancelSubscriptionButton,
        }}
        handlePrimaryButtonClick={() => handleSuccessModalClose('Done')}
        size="large"
      >
        <SuccessModalContent />
      </Modal>
    </Stack>
  );
}

type PlansDetailsProps = {
  currentPlan: SubscriptionPlan;
  selectedPlan: SubscriptionPlan;
};

function PlansDetails({currentPlan, selectedPlan}: PlansDetailsProps) {
  const planName = (plan: SubscriptionPlan) =>
    `${plan.bundle.valueOf()} ${
      plan.bundle === ContentLabPlanBundle.PRO ? `/${plan.videos} credits` : ''
    }`;
  const priceText = (plan: SubscriptionPlan) => {
    return `${getPriceCurrency(plan.currency, plan.price)} 
        ${translate('/mo')} 
        ${
          plan.duration === Duration.YEARLY
            ? `(${translate('billed annually')})`
            : ''
        }`;
  };
  return (
    <Stack className={styles.plansDetailsContainer}>
      <DetailsRow
        rightText={translate('From')}
        rightColor={designSystemToken('semantic.fg.secondary')}
        secondRightText={planName(currentPlan)}
        secondRightColor={designSystemToken('semantic.fg.primary')}
        leftText={priceText(currentPlan)}
        leftColor={designSystemToken('semantic.fg.secondary')}
        className={styles.planDetails}
      />
      <Divider margin={0} />
      <DetailsRow
        rightText={translate('To')}
        rightColor={designSystemToken('semantic.fg.secondary')}
        secondRightText={planName(selectedPlan)}
        secondRightColor={designSystemToken('semantic.fg.primary')}
        leftText={priceText(selectedPlan)}
        leftColor={designSystemToken('semantic.fg.secondary')}
        className={styles.planDetails}
      />
    </Stack>
  );
}

type DetailsRowProps = {
  rightText: string;
  rightColor: string;
  secondRightText?: string;
  secondRightColor?: string;
  leftText: string;
  leftColor: string;
  isLoading?: boolean;
  className?: string;
};

function DetailsRow({
  rightText,
  rightColor,
  secondRightText,
  secondRightColor,
  leftText,
  leftColor,
  isLoading = false,
  className,
}: DetailsRowProps) {
  return (
    <Stack direction="row" className={`${styles.detailsRow} ${className}`}>
      <Stack direction="row" gap="4px">
        <Body size="sm" color={rightColor}>
          {rightText}
        </Body>
        <Conditional
          condition={
            secondRightText !== undefined && secondRightColor !== undefined
          }
        >
          <Body size="sm" color={secondRightColor}>
            {secondRightText}
          </Body>
        </Conditional>
      </Stack>
      <Conditional
        condition={!isLoading}
        fallback={
          <Skeleton
            variant="rounded"
            width="48px"
            height="16px"
            borderRadius="4px"
          />
        }
      >
        <Body size="sm" color={leftColor}>
          {leftText}
        </Body>
      </Conditional>
    </Stack>
  );
}

type PriceSectionProps = {
  isLoading: boolean;
  selectedPlan: SubscriptionPlan;
  unusedVideosCount?: number;
  unusedVideosValue?: number;
};

function PriceSection({
  isLoading,
  selectedPlan,
  unusedVideosCount,
  unusedVideosValue,
}: PriceSectionProps) {
  return (
    <Stack className={styles.priceSectionContainer}>
      <Conditional condition={isLoading || (unusedVideosValue || 0) > 0}>
        <Stack direction="column" gap="8px">
          <DetailsRow
            rightText={translate('Business plan')}
            rightColor={designSystemToken('semantic.fg.primary')}
            leftText={getPriceCurrency(
              selectedPlan.currency,
              selectedPlan.duration === Duration.YEARLY
                ? selectedPlan.yearlyPrice || selectedPlan.price * 12
                : selectedPlan.price
            )}
            leftColor={designSystemToken('semantic.fg.primary')}
          />
          <DetailsRow
            rightText={translate('Unused video credits')}
            rightColor={designSystemToken('semantic.fg.primary')}
            secondRightText={!isLoading ? `(${unusedVideosCount})` : ''}
            secondRightColor={designSystemToken('semantic.fg.secondary')}
            leftText={`- ${getPriceCurrency(
              selectedPlan.currency,
              unusedVideosValue
            )}`}
            leftColor={designSystemToken('semantic.fg.success')}
            isLoading={isLoading}
          />
          <Divider margin={0} />
        </Stack>
      </Conditional>
      <Stack
        direction="row"
        style={{marginTop: '8px'}}
        className={styles.detailsRow}
      >
        <Stack direction="row" gap="2px">
          <Body size="md" color={designSystemToken('semantic.fg.primary')}>
            {translate('Total amount due')}
          </Body>
        </Stack>
        <Conditional
          condition={!isLoading}
          fallback={
            <Skeleton
              variant="rounded"
              width="82px"
              height="16px"
              borderRadius="4px"
            />
          }
        >
          <Body
            size="md"
            color={designSystemToken('semantic.fg.primary')}
            style={{fontWeight: 700}}
          >
            {getPriceCurrency(
              selectedPlan.currency,
              (selectedPlan.duration === Duration.YEARLY
                ? selectedPlan.yearlyPrice || selectedPlan.price * 12
                : selectedPlan.price) - (unusedVideosValue || 0)
            )}
          </Body>
        </Conditional>
      </Stack>
    </Stack>
  );
}

type DisclaimerProps = {
  shouldShowUnusedCredits: boolean;
};

function Disclaimer({shouldShowUnusedCredits}: DisclaimerProps) {
  return (
    <Stack
      direction="column"
      gap="8px"
      style={{
        marginTop: '16px',
        alignItems: 'flex-start',
      }}
    >
      <Body size="xs" color={designSystemToken('semantic.fg.secondary')}>
        {translate('All prices are presented and charged in US Dollars')}
      </Body>
      <Conditional condition={shouldShowUnusedCredits}>
        <Body
          size="xs"
          color={designSystemToken('semantic.fg.secondary')}
          style={{textAlign: 'start'}}
        >
          {translate(
            'The unused credit balance above is accurate as of this moment, and may change at the time of billing'
          )}
        </Body>
      </Conditional>
    </Stack>
  );
}

function ErrorMessage() {
  return (
    <Alert
      severity="error"
      style={{borderRadius: '8px', padding: '0 16px', marginTop: '16px'}}
    >
      <Body size="xs" textAlign="start">
        <Body component="span" size="xs" style={{fontWeight: 700}}>
          {translate("Couldn't update subscription.")}
        </Body>{' '}
        <Body component="span" size="xs" style={{fontWeight: 400}}>
          {translate(
            'Please try again or contact support if the issue persists.'
          )}
        </Body>
      </Body>
    </Alert>
  );
}

type ButtonsProps = {
  handleConfirmChangeClick: () => void;
  handleCancelClick: () => void;
  isLoading: boolean;
};

function Buttons({
  handleConfirmChangeClick,
  handleCancelClick,
  isLoading,
}: ButtonsProps) {
  return (
    <Stack direction="column" style={{width: '100%'}}>
      <PricingCtaButton
        isDisabled={false}
        isLoading={isLoading}
        onClick={!isLoading ? handleConfirmChangeClick : () => {}}
        text={translate('Confirm Change')}
      />
      <Button
        appearance="neutral"
        mode="plain"
        size="medium"
        onClick={handleCancelClick}
        style={{alignSelf: 'center', marginTop: '24px', height: '18px'}}
      >
        <Label
          size="md"
          color={designSystemToken('semantic.bg.neutral')}
          style={{fontSize: '12px', fontWeight: '700'}}
        >
          {translate('Cancel')}
        </Label>
      </Button>
    </Stack>
  );
}

function SuccessModalContent() {
  return (
    <Stack direction="column" gap="16px" alignItems="center">
      <Icon size="medium" appearance="brand" name="Actions-Patch-Accept-Line" />
      <Headline size="md">{translate('Upgrade Complete!')}</Headline>
      <Stack direction="column" gap="2px">
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate('Your plan has been successfully upgraded.')}
        </Body>
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate('You now have access to enhanced features and credits.')}
        </Body>
      </Stack>
    </Stack>
  );
}

export default SubscriptionChangeContent;
